import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { getAuthHeaders } from '../utils/getAuthHeaders'
import { getErrorMessage, BACKEND_FAIL_MESSAGE } from '../utils/getErrorMessage'
import { endpoints } from "../config/api";
import MultiSelectDropdown from "./MultiSelectDropdown";
import { refreshAgentCache } from "../utils/refreshAgentCache";

// Convert minutes to hours for display/editing
const minutesToHours = (minutes) => {
  if (minutes == null || minutes === "" || isNaN(Number(minutes))) return "0.00";
  return (Number(minutes) / 60).toFixed(2);
};
// Convert hours (string) back to minutes for API
const hoursToMinutes = (hoursStr) => {
  const parsed = parseFloat(hoursStr);
  if (hoursStr === "" || isNaN(parsed) || parsed < 0) return 0;
  return Math.round(parsed * 60);
};

const ModifyLibraryAgents = ({ onClose, onEditAgent }) => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const tableContainerRef = useRef(null);
  const [hoursSavedEditable, setHoursSavedEditable] = useState(false);
  const [hoursSavedValues, setHoursSavedValues] = useState({});
  const [orgVisibilityEditable, setOrgVisibilityEditable] = useState(false);
  const [orgVisibilityValues, setOrgVisibilityValues] = useState({});
  const [availableOrgs, setAvailableOrgs] = useState([]);
  const [savingHours, setSavingHours] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteModalAgent, setDeleteModalAgent] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchAllLibraryAgents();
    fetchOrgs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchOrgs = async () => {
    try {
      const response = await axios.get(
        endpoints.orgs,
        { headers: { ...getAuthHeaders() } }
      );
      setAvailableOrgs(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error("Error fetching orgs", err);
    }
  };

  const normalizeOrgId = (value) => {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const getAgentOrgIds = (agent) => {
    const orgs = Array.isArray(agent?.organizations) ? agent.organizations : [];
    return orgs
      .map((org) => {
        if (typeof org === "number" || typeof org === "string") return normalizeOrgId(org);
        return normalizeOrgId(org?.id ?? org?.organization_id);
      })
      .filter((id) => id != null);
  };

  const areSameIds = (a, b) => {
    const aa = [...(a || [])].map(Number).filter((x) => !Number.isNaN(x)).sort((x, y) => x - y);
    const bb = [...(b || [])].map(Number).filter((x) => !Number.isNaN(x)).sort((x, y) => x - y);
    if (aa.length !== bb.length) return false;
    return aa.every((v, idx) => v === bb[idx]);
  };

  const getOrgDisplayName = (orgId) => {
    const id = normalizeOrgId(orgId);
    if (id == null) return String(orgId);
    const fromApi = availableOrgs.find((o) => Number(o.id) === id);
    return fromApi?.name ?? `Org ${id}`;
  };

  const getOrgVisibilityLabel = (agent) => {
    const orgs = Array.isArray(agent?.organizations) ? agent.organizations : [];
    if (orgs.length === 0) return "Shown in all";
    return orgs
      .map((org) => {
        if (typeof org === "string" || typeof org === "number") {
          return getOrgDisplayName(org);
        }
        return org?.name || org?.org_name || getOrgDisplayName(org?.id ?? org?.organization_id);
      })
      .filter(Boolean)
      .join(", ");
  };

  const fetchAllLibraryAgents = async () => {
    const authHeaders = getAuthHeaders();
    if (!authHeaders.Authorization) {
      setError("Please log in to view library agents.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      let allAgents = [];
      let cursor = null;

      do {
        const response = await axios.get(
          endpoints.agentsLibraryAdmin,
          {
            headers: {
              ...authHeaders,
              Accept: "*/*",
              "Content-Type": "application/json",
            },
            params: cursor ? { cursor } : {},
          }
        );
        const newAgents = response.data?.data || [];
        cursor = response.data?.nextCursor || null;
        allAgents = [...allAgents, ...newAgents];
        setAgents(allAgents);
      } while (cursor);

    } catch (err) {
      console.error("Error fetching library agents", err);
      setError(getErrorMessage(err, "Failed to fetch library agents") + ". " + BACKEND_FAIL_MESSAGE);
    } finally {
      setLoading(false);
    }
  };

  const handleScroll = () => {
    // All agents loaded at once; scroll is for table view only
  };

  const handleEditHoursSavedClick = () => {
    const initial = {};
    agents.forEach((agent) => {
      initial[agent.id] = minutesToHours(agent.minutes_saved);
    });
    setHoursSavedValues(initial);
    setHoursSavedEditable(true);
  };

  const handleEditOrgVisibilityClick = () => {
    const initial = {};
    agents.forEach((agent) => {
      initial[agent.id] = getAgentOrgIds(agent);
    });
    setOrgVisibilityValues(initial);
    setOrgVisibilityEditable(true);
  };

  const handleHoursSavedChange = (agentId, value) => {
    setHoursSavedValues((prev) => ({ ...prev, [agentId]: value }));
  };

  const handleSaveHoursSaved = async () => {
    setSavingHours(true);
    setError("");
    const results = { success: 0, failed: 0 };
    for (const agent of agents) {
      const hoursStr = hoursSavedValues[agent.id] ?? minutesToHours(agent.minutes_saved);
      const newMinutes = hoursToMinutes(hoursStr);
      const originalMinutes = agent.minutes_saved != null ? Number(agent.minutes_saved) : 0;
      const originalOrgIds = getAgentOrgIds(agent);
      const newOrgIds = orgVisibilityValues[agent.id] ?? originalOrgIds;
      const orgChanged = areSameIds(newOrgIds, originalOrgIds) === false;
      const minutesChanged = newMinutes !== originalMinutes;
      if (!minutesChanged && !orgChanged) continue;

      try {
        const payload = {
          agent_library_id: agent.id,
        };
        if (minutesChanged) payload.minutes_saved = newMinutes;
        if (orgChanged) payload.organization_ids = newOrgIds;
        await axios.post(
          endpoints.agentsEditAgentDetails,
          payload,
          {
            headers: {
              ...getAuthHeaders(),
              "Content-Type": "application/json",
            },
          }
        );
        results.success += 1;
      } catch (err) {
        console.error(`Error updating hours saved for agent ${agent.id}`, err);
        results.failed += 1;
      }
    }
    setSavingHours(false);
    setHoursSavedEditable(false);
    setOrgVisibilityEditable(false);
    if (results.failed > 0) {
      setError(`Saved ${results.success} agents. Failed: ${results.failed}. Check console.`);
    }
    if (results.success > 0) {
      await refreshAgentCache();
      fetchAllLibraryAgents();
    }
  };

  const handleDeleteClick = (agent) => {
    setDeleteModalAgent(agent);
    setDeleteConfirmText("");
  };

  const handleDeleteCancel = () => {
    setDeleteModalAgent(null);
    setDeleteConfirmText("");
  };

  const handleDeleteConfirm = async () => {
    if ((deleteConfirmText || "").trim().toLowerCase() !== "delete") return;
    if (!deleteModalAgent?.id) return;
    setDeleting(true);
    setError("");
    try {
      await axios.delete(endpoints.agentsLibraryDelete, {
        data: { agentId: deleteModalAgent.id },
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
      });
      await refreshAgentCache();
      setDeleteModalAgent(null);
      setDeleteConfirmText("");
      fetchAllLibraryAgents();
    } catch (err) {
      console.error("Error deleting library agent", err);
      setError(getErrorMessage(err, "Failed to delete library agent") + ". " + BACKEND_FAIL_MESSAGE);
    } finally {
      setDeleting(false);
    }
  };

  const tableStyle = {
    width: "100%",
    tableLayout: "fixed",
    borderCollapse: "collapse",
    marginTop: "12px",
    backgroundColor: "#ffffff",
    borderRadius: "8px",
    overflow: "visible",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    fontSize: "13px",
    lineHeight: 1.35,
  };

  const thStyle = {
    backgroundColor: "#3730a3",
    color: "#ffffff",
    padding: "6px 8px",
    textAlign: "left",
    fontWeight: "600",
    fontSize: "12px",
    borderBottom: "2px solid #312e81",
  };

  const tdStyle = {
    padding: "6px 8px",
    borderBottom: "1px solid #e5e7eb",
    color: "#1f2937",
  };

  const trHoverStyle = {
    backgroundColor: "#f9fafb",
  };

  const trChangedStyle = {
    backgroundColor: "#dbeafe",
  };

  const rowHeight = "36px";
  const closeButtonStyle = {
    height: rowHeight,
    padding: "0 16px",
    boxSizing: "border-box",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#3730a3",
    color: "#ffffff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "600",
  };

  const errorMessageStyle = {
    color: "#ef4444",
    marginBottom: "16px",
    padding: "12px",
    backgroundColor: "#fee2e2",
    borderRadius: "6px",
    border: "1px solid #ef4444"
  };

  const editIconButtonStyle = {
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    padding: "2px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: "6px",
    borderRadius: "4px",
    transition: "background-color 0.2s"
  };

  const deleteIconButtonStyle = {
    ...editIconButtonStyle,
    marginRight: 0,
    marginLeft: "4px",
  };

  const modalOverlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  };

  const modalContentStyle = {
    backgroundColor: "#fff",
    borderRadius: "8px",
    padding: "24px",
    maxWidth: "420px",
    width: "90%",
    boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
  };

  const modalTitleStyle = {
    fontSize: "18px",
    fontWeight: "700",
    color: "#1f2937",
    marginBottom: "8px",
  };

  const modalBodyStyle = {
    fontSize: "14px",
    color: "#4b5563",
    marginBottom: "16px",
  };

  const modalInputStyle = {
    width: "100%",
    padding: "10px 12px",
    fontSize: "14px",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    boxSizing: "border-box",
    marginBottom: "16px",
  };

  const modalDangerInputStyle = {
    ...modalInputStyle,
    borderColor: "#ef4444",
  };

  const modalButtonsStyle = {
    display: "flex",
    gap: "12px",
    justifyContent: "flex-end",
  };

  const modalCancelButtonStyle = {
    padding: "8px 16px",
    fontSize: "14px",
    backgroundColor: "#e5e7eb",
    color: "#374151",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "600",
  };

  const modalDeleteButtonStyle = {
    padding: "8px 16px",
    fontSize: "14px",
    backgroundColor: "#ef4444",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "600",
  };

  const modalDeleteButtonDisabledStyle = {
    ...modalDeleteButtonStyle,
    opacity: 0.5,
    cursor: "not-allowed",
  };

  const topButtonStyle = {
    ...closeButtonStyle,
    marginRight: "10px",
    height: rowHeight,
  };

  const shimmerRowCount = 10;

  return (
    <div style={{ width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box" }}>
      <style>{`
        @keyframes modify-library-shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .modify-library-shimmer-cell {
          background: linear-gradient(90deg, #e5e7eb 0%, #f3f4f6 50%, #e5e7eb 100%);
          background-size: 200% 100%;
          animation: modify-library-shimmer 1.5s ease-in-out infinite;
          border-radius: 3px;
          height: 16px;
        }
      `}</style>
      <div style={{ marginBottom: "12px", display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px" }}>
        <button style={closeButtonStyle} onClick={onClose}>
          Close
        </button>
        <div style={{ position: "relative", flex: "1", minWidth: "280px", maxWidth: "560px", height: rowHeight }}>
          <input
            type="text"
            placeholder="Search agents by name, category, description, or ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: "100%",
              height: "100%",
              padding: "0 36px 0 10px",
              fontSize: "13px",
              border: "1px solid #3730a3",
              borderRadius: "6px",
              outline: "none",
              boxSizing: "border-box",
            }}
          />
          <span style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: "#6b7280", display: "flex", alignItems: "center" }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
          </span>
        </div>
        <div style={{ display: "flex", gap: "10px", marginLeft: "auto" }}>
          {!(hoursSavedEditable || orgVisibilityEditable) ? (
            <>
              <button
                style={topButtonStyle}
                onClick={handleEditOrgVisibilityClick}
                disabled={loading || agents.length === 0}
              >
                Edit Org Visibility
              </button>
              <button
                style={topButtonStyle}
                onClick={handleEditHoursSavedClick}
                disabled={loading || agents.length === 0}
              >
                Edit Hours Saved
              </button>
            </>
          ) : (
            <>
              <button
                style={{ ...topButtonStyle, backgroundColor: "#059669" }}
                onClick={handleSaveHoursSaved}
                disabled={savingHours}
              >
                {savingHours ? "Saving…" : "Save"}
              </button>
              <button
                style={{ ...topButtonStyle, backgroundColor: "#6b7280" }}
                onClick={() => {
                  setHoursSavedEditable(false);
                  setHoursSavedValues({});
                  setOrgVisibilityEditable(false);
                  setOrgVisibilityValues({});
                }}
                disabled={savingHours}
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </div>

      {loading && (
        <div style={{ overflow: "hidden", borderRadius: "8px", border: "1px solid #e5e7eb", marginTop: "20px" }}>
          <table style={{ ...tableStyle, marginTop: 0 }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, width: "14%" }}>ID</th>
                <th style={{ ...thStyle, width: "13%" }}>Name</th>
                <th style={{ ...thStyle, width: "7%" }}>Category</th>
                <th style={{ ...thStyle, width: "28%" }}>Description</th>
                <th style={{ ...thStyle, width: "26%" }}>Org Visibility</th>
                <th style={{ ...thStyle, width: "5%", whiteSpace: "nowrap" }} title="Hours saved">Hrs</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: shimmerRowCount }).map((_, i) => (
                <tr key={i}>
                  <td style={tdStyle}><div className="modify-library-shimmer-cell" style={{ width: "60%", maxWidth: "120px" }} /></td>
                  <td style={tdStyle}><div className="modify-library-shimmer-cell" style={{ width: "80%", maxWidth: "160px" }} /></td>
                  <td style={tdStyle}><div className="modify-library-shimmer-cell" style={{ width: "50%", maxWidth: "100px" }} /></td>
                  <td style={tdStyle}><div className="modify-library-shimmer-cell" style={{ width: "90%", maxWidth: "240px" }} /></td>
                  <td style={tdStyle}><div className="modify-library-shimmer-cell" style={{ width: "90%", maxWidth: "280px" }} /></td>
                  <td style={{ ...tdStyle, width: "1%", maxWidth: "56px" }}><div className="modify-library-shimmer-cell" style={{ width: "36px", maxWidth: "100%" }} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {error && <div style={errorMessageStyle}>{error}</div>}

      {!loading && !error && agents.length > 0 && (() => {
        const q = searchQuery.trim().toLowerCase();
        const filteredAgents = q
          ? agents.filter(
              (agent) =>
                (agent.name || "").toLowerCase().includes(q) ||
                (agent.category || "").toLowerCase().includes(q) ||
                (agent.description || "").toLowerCase().includes(q) ||
                (agent.id || "").toLowerCase().includes(q)
            )
          : agents;
        const minVisibleRows = 5;
        const approxRowHeightPx = 38;
        const headerHeightPx = 42;
        const minTableHeightPx = headerHeightPx + minVisibleRows * approxRowHeightPx;
        return filteredAgents.length > 0 ? (
        <div
          ref={tableContainerRef}
          style={{
            width: "100%",
            maxWidth: "100%",
            overflowX: "hidden",
            overflowY: orgVisibilityEditable ? "visible" : "auto",
            maxHeight: orgVisibilityEditable ? "none" : "70vh",
            minHeight: `${minTableHeightPx}px`,
          }}
          onScroll={handleScroll}
        >
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={{ ...thStyle, width: "14%" }}>ID</th>
                <th style={{ ...thStyle, width: "13%" }}>Name</th>
                <th style={{ ...thStyle, width: "7%" }}>Category</th>
                <th style={{ ...thStyle, width: "28%" }}>Description</th>
                <th style={{ ...thStyle, width: "26%" }}>Org Visibility</th>
                <th style={{ ...thStyle, width: "5%", whiteSpace: "nowrap" }} title="Hours saved">Hrs</th>
              </tr>
            </thead>
            <tbody>
              {filteredAgents.map((agent) => {
                const hoursDisplay = minutesToHours(agent.minutes_saved);
                const hoursValue = hoursSavedEditable
                  ? (hoursSavedValues[agent.id] ?? hoursDisplay)
                  : hoursDisplay;
                const originalMinutes = agent.minutes_saved != null ? Number(agent.minutes_saved) : 0;
                const currentMinutes = hoursToMinutes(hoursValue);
                const originalOrgIds = getAgentOrgIds(agent);
                const currentOrgIds = orgVisibilityEditable
                  ? (orgVisibilityValues[agent.id] ?? originalOrgIds)
                  : originalOrgIds;
                const orgChanged = orgVisibilityEditable && !areSameIds(currentOrgIds, originalOrgIds);
                const isChanged = (hoursSavedEditable && currentMinutes !== originalMinutes) || orgChanged;
                return (
                  <tr key={agent.id} style={isChanged ? trChangedStyle : trHoverStyle}>
                    <td style={{ ...tdStyle, fontSize: "11px", wordBreak: "break-all", color: "#4b5563" }}>
                      <button
                        type="button"
                        onClick={() => onEditAgent && onEditAgent(agent.id)}
                        style={editIconButtonStyle}
                        onMouseEnter={(e) => { e.target.style.backgroundColor = "#e5e7eb"; }}
                        onMouseLeave={(e) => { e.target.style.backgroundColor = "transparent"; }}
                        title="Edit Agent"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#3730a3"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                        </svg>
                      </button>
                      <span title={agent.id}>{agent.id}</span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                        {agent.name}
                        <button
                          type="button"
                          onClick={() => handleDeleteClick(agent)}
                          style={deleteIconButtonStyle}
                          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#fee2e2"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
                          title="Delete library agent"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 6h18" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            <line x1="10" y1="11" x2="10" y2="17" />
                            <line x1="14" y1="11" x2="14" y2="17" />
                          </svg>
                        </button>
                      </span>
                    </td>
                    <td style={tdStyle}>{agent.category}</td>
                    <td style={{ ...tdStyle, overflow: "hidden", textOverflow: "ellipsis" }} title={agent.description}>{agent.description}</td>
                    <td style={{ ...tdStyle, verticalAlign: "top", overflow: "hidden" }}>
                      {orgVisibilityEditable ? (() => {
                        const selectedIds = orgVisibilityValues[agent.id] ?? originalOrgIds;
                        const apiIds = new Set(availableOrgs.map((o) => Number(o.id)));
                        const orphanIds = selectedIds.filter((id) => !apiIds.has(Number(id)));
                        const orgSelectOptions = [
                          ...availableOrgs.map((o) => ({ value: String(o.id), label: o.name })),
                          ...orphanIds.map((id) => ({
                            value: String(id),
                            label: `${getOrgDisplayName(id)} (id ${id})`,
                          })),
                        ];
                        return (
                          <div style={{ width: "100%", maxWidth: "100%" }}>
                            <MultiSelectDropdown
                              options={orgSelectOptions}
                              value={selectedIds.map((id) => String(id))}
                              onChange={(vals) =>
                                setOrgVisibilityValues((prev) => ({
                                  ...prev,
                                  [agent.id]: vals.map((v) => normalizeOrgId(v)).filter((id) => id != null),
                                }))
                              }
                              placeholder="Nothing Selected means Shown to All"
                              searchable
                              optionColumns={2}
                              aria-label="Organisation visibility"
                            />
                          </div>
                        );
                      })() : (
                        <span title={getOrgVisibilityLabel(agent)} style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {getOrgVisibilityLabel(agent)}
                        </span>
                      )}
                    </td>
                    <td style={{ ...tdStyle, fontWeight: "bold", width: "1%", maxWidth: "56px", textAlign: "right", whiteSpace: "nowrap", paddingLeft: "6px", paddingRight: "8px" }}>
                      {hoursSavedEditable ? (
                        <input
                          type="text"
                          value={hoursValue}
                          onChange={(e) => handleHoursSavedChange(agent.id, e.target.value)}
                          style={{
                            width: "44px",
                            padding: "4px 4px",
                            fontSize: "12px",
                            border: "1px solid #d1d5db",
                            borderRadius: "4px",
                            boxSizing: "border-box",
                          }}
                          placeholder="0.00"
                        />
                      ) : (
                        hoursDisplay
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        ) : (
          <p style={{ padding: "12px", color: "#6b7280" }}>No agents match your search.</p>
        );
      })()}

      {!loading && !error && agents.length === 0 && (
        <p>No library agents found</p>
      )}

      {deleteModalAgent && (
        <div style={modalOverlayStyle} onClick={handleDeleteCancel}>
          <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
            <div style={modalTitleStyle}>Delete library agent</div>
            <p style={modalBodyStyle}>
              Are you sure you want to delete this library agent from the database? This action cannot be undone.
            </p>
            <p style={{ ...modalBodyStyle, marginBottom: "8px" }}>
              Type <strong>delete</strong> to confirm:
            </p>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="delete"
              style={modalDangerInputStyle}
              autoFocus
              aria-label="Type delete to confirm"
            />
            <div style={modalButtonsStyle}>
              <button type="button" style={modalCancelButtonStyle} onClick={handleDeleteCancel} disabled={deleting}>
                Cancel
              </button>
              <button
                type="button"
                style={(deleteConfirmText || "").trim().toLowerCase() === "delete" ? modalDeleteButtonStyle : modalDeleteButtonDisabledStyle}
                onClick={handleDeleteConfirm}
                disabled={deleting || (deleteConfirmText || "").trim().toLowerCase() !== "delete"}
              >
                {deleting ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModifyLibraryAgents;
